import React, { createContext, Dispatch, JSX, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import Sidebar, { SidebarItem } from '../api/Sidebar';
import useScreenSize from '../hooks/UseScreenSize';
import { ScreenSize } from '../types/ScreenSize';
import { logger } from '../util/Logger';
import useIsMobileDevice from 'hooks/UseIsMobileDevice';

const SIDEBAR_WIDTH = {
  Open: 400,
  Closed: 50,
  Hidden: 0,
};

const PREVIEW_BREAKPOINT = ScreenSize['2xl'];

export enum SidebarRightMode {
  Preview = 'Preview',
  Default = 'Default',
}

interface Props {
  children: React.ReactNode;
}

type SidebarRightContextType = {
  width: number;
  mode: SidebarRightMode;
  sidebar: Sidebar;
  items: SidebarItem[];
  itemCount: number;
  isOpen: boolean | undefined;
  setIsOpen: Dispatch<SetStateAction<boolean | undefined>>;
  isHidden: boolean;
  setIsHidden: Dispatch<SetStateAction<boolean>>;
  preview: ReactNode | undefined;
  setPreview: Dispatch<SetStateAction<ReactNode | undefined>>;
  previewBreakpoint: number;
};

const SidebarRightContext = createContext<SidebarRightContextType>({
  width: 0,
  mode: SidebarRightMode.Default,
  sidebar: {} as Sidebar,
  items: [],
  itemCount: 0,
  isOpen: false,
  setIsOpen: () => logger.warn('no SidebarRight provider'),
  isHidden: false,
  setIsHidden: () => logger.warn('no SidebarRight provider'),
  preview: undefined,
  setPreview: () => logger.warn('no SidebarRight provider'),
  previewBreakpoint: PREVIEW_BREAKPOINT,
});

export function useSidebarRightContext(): SidebarRightContextType {
  return useContext(SidebarRightContext);
}

export function SidebarRightProvider({ children }: Props): JSX.Element {
  const [items, setItems] = useState<SidebarItem[]>([]); // holds all the sidebar items
  const [isOpen, setIsOpen] = useState<boolean>();
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [preview, setPreview] = useState<SidebarRightContextType['preview']>(undefined);

  const { width } = useScreenSize();
  const isMobileDevice = useIsMobileDevice();

  const sidebarWidth = useMemo(() => {
    // if there is no preview and no items, we should hide the sidebar
    if (!preview && items.length === 0) {
      return 0;
    }

    if (preview) {
      return width > ScreenSize['4xl'] ? 1000 : width > ScreenSize['3xl'] ? 800 : width > ScreenSize['2xl'] ? 600 : 400;
    } else {
      if (isOpen) {
        return SIDEBAR_WIDTH.Open;
      }

      if (!isMobileDevice) {
        return SIDEBAR_WIDTH.Closed;
      }

      return SIDEBAR_WIDTH.Hidden;
    }
  }, [isMobileDevice, isOpen, items, preview, width]);

  /**
   * Open the sidebar when the size of the window is acceptable (only after a resize has been placed)
   * Or the open_context_sidebar session key has been set
   */
  useEffect(() => {
    if (width === undefined) return;

    const openFromSession = sessionStorage.getItem('open_context_sidebar');
    if (isOpen === undefined && openFromSession === '1') {
      // FIXME: REACT 19 - disable for now Error: Calling setState synchronously within an effect can trigger cascading renders
      // eslint-disable-next-line
      setIsOpen(true);
    } else {
      setIsOpen(width > ScreenSize['3xl']);
    }
  }, [width]); //eslint-disable-line

  /**
   * Sideffect for the setIsOpen
   * Save the state to the sessionStorage
   */
  useEffect(() => {
    // to avoid the double re-render from the strictMode, we should check if isOpen has an actual value
    if (isOpen === undefined) return;

    // if we set the sidebar to OPEN, we should save this state to the sessionStorage
    // but only if the value is has not been set
    const openFromSession = sessionStorage.getItem('open_context_sidebar');
    if (isOpen === true && openFromSession !== '1') {
      sessionStorage.setItem('open_context_sidebar', '1');
    } else {
      // all other cases we should get rid of this key in the session
      sessionStorage.removeItem('open_context_sidebar');
    }
  }, [isOpen]);

  // construct a new Sidebar with a update callback.
  // when invoked, it will update the list of items
  const [sidebar] = useState<Sidebar>(new Sidebar(_items => setItems(_items)));

  return (
    <SidebarRightContext.Provider
      value={{
        width: sidebarWidth,
        mode: preview ? SidebarRightMode.Preview : SidebarRightMode.Default,
        sidebar,
        items,
        itemCount: items.length,
        isOpen,
        setIsOpen,
        isHidden,
        setIsHidden,
        preview,
        setPreview,
        previewBreakpoint: PREVIEW_BREAKPOINT,
      }}
    >
      {children}
    </SidebarRightContext.Provider>
  );
}
